import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';

import React, { useRef, useState } from 'react';

import useBundleTranslation from 'i18n';

import IconMi from 'components/common/icon/IconMi';
import { Button } from '@mui/material';

import { editorExternalReferenceAPI } from 'api/editor/external-reference';
import { APIResponse } from 'tools/types';
import { ReferenceInfoResponse } from 'components/external-reference-selection';
import { FormComponentBuilder } from 'components/common/form/layout';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useQueryClient } from '@tanstack/react-query';
import { useFormState } from 'react-hook-form';

export interface FilterUsageRefreshButtonControlProps extends FormControlProps {
    profileId: number;
    referenceId: number;
    filterId: number;
    gridToRefresh: string;
}

export default function FilterUsageRefreshButton({
    controlProps,
}: FormElementControlPropsType<FilterUsageRefreshButtonControlProps>) {
    const { t } = useBundleTranslation(['components/external-reference/editor/reference_external_filters_manager']);

    // Display Loading Mask
    const [isCollectionRunning, setIsCollectionRunning] = useState<boolean>(false);

    const { isDirty } = useFormState({ control: controlProps.form.hookFormControl });

    const savedFilterId = useRef(0);
    const doRefresh = () => {
        const idToUse = savedFilterId.current != 0 ? savedFilterId.current : controlProps.filterId;
        setIsCollectionRunning(true);
        editorExternalReferenceAPI
            .refreshFilterValues(controlProps.profileId, controlProps.referenceId, idToUse)
            .then((_response: APIResponse<ReferenceInfoResponse>) => {
                reloadGridData();
            })
            .finally(() => {
                setIsCollectionRunning(false);
            });
    };

    const handleRefreshAutoFilters = () => {
        if (isDirty) {
            controlProps.form.hookFormSetValue('refreshList', 1, { shouldDirty: false });
            requestAnimationFrame(() => {
                controlProps.form.formSave().then((saveResponse: any) => {
                    if (saveResponse?.data?.status === 'OK') {
                        savedFilterId.current = saveResponse.data.id;
                        doRefresh();
                    }
                });
            });
        } else {
            doRefresh();
        }
    };

    const queryClient = useQueryClient();
    const reloadGridData = () => {
        queryClient.invalidateQueries({ queryKey: [controlProps.gridToRefresh] });
    };

    return (
        <>
            {isCollectionRunning && <LoadingPlaceholder />}
            <Button
                startIcon={<IconMi icon={'refresh-cw'} />}
                variant={'outlined'}
                onClick={() => {
                    handleRefreshAutoFilters();
                }}
            >
                {(isDirty ? `${t('save')} & ` : '') + t('refresh_filter_values')}
            </Button>
        </>
    );
}

export class FilterUsageRefreshButtonBuilder extends FormComponentBuilder {
    prepareProps(): FilterUsageRefreshButtonControlProps {
        return {
            ...this.controlProps,
            profileId: Number(this.elementProps.componentProps?.profileId ?? 0),
            referenceId: Number(this.elementProps.componentProps?.referenceId ?? 0),
            filterId: Number(this.elementProps.componentProps?.filterId ?? 0),
            gridToRefresh: this.elementProps.componentProps?.gridToRefresh ?? '',
        };
    }
}
